/**
 * useAccountLiveData — fetches per-account trades, decisions, equity, state.
 *
 * Triggers on every `accountId` change so the Simulation Lab can show
 * real-per-account history (fixes the "static graph + static trade log"
 * bug where selecting different cards never updated the panels below).
 *
 * Auto-polls every POLL_INTERVAL_MS so balances, trades, and the equity
 * graph update live while the autopilot is placing trades.
 *
 * All endpoints are public (no auth). See execution-integration/backend/routes/brain.py.
 *
 *   GET /api/v1/account/{id}/trades?limit=200
 *   GET /api/v1/account/{id}/decisions?limit=100
 *   GET /api/v1/account/{id}/equity
 *   GET /api/v1/account/{id}/state
 */
import { useState, useEffect, useCallback, useRef } from 'react';

import { apiV1Base } from '../lib/apiConfig';
const POLL_INTERVAL_MS = 10_000;

const BASE_URL = apiV1Base();

export interface BrainTrade {
  trade_id: string;
  decision_id: string;
  account_id: string;
  symbol: string;
  side: string;
  quantity: number;
  price: number | null;
  status: string;
  broker: string | null;
  execution_id: string | null;
  error: string | null;
  executed_at: string | null;
  created_at: string;
  /** Joined fields from the linked decision row (present when decisions_store returns them). */
  decision_tier?: 'L0' | 'L1' | 'L2' | null;
  decision_summary?: string | null;
  decision_confidence?: number | null;
  decision_action?: string | null;
}

export interface BrainDecision {
  decision_id: string;
  account_id: string;
  user_id: string | null;
  tier: 'L0' | 'L1' | 'L2';
  symbol: string;
  action: string;
  confidence: number;
  summary: string | null;
  debate: {
    bull?: string;
    bear?: string;
    moderator?: string;
    disagreements?: string[];
  } | null;
  signal: Record<string, unknown> | null;
  trade_id: string | null;
  outcome: 'pending' | 'executed' | 'blocked' | 'error' | 'system_issue' | 'skipped';
  reason: string | null;
  created_at: string;
}

/**
 * A single Alpaca-sourced equity point. Shape matches the backend:
 *   { t: ISO8601, equity: number }
 */
export interface BrainEquityPoint {
  t: string;
  equity: number;
}

/**
 * Response of GET /account/{id}/equity?range=...
 *
 *   { account_id, range, period, timeframe, points: [{t, equity}], source: "alpaca" }
 *
 * No derived / reconstructed fields. If Alpaca has no history for the
 * selected range, `points` is an empty array.
 */
export type EquityRange = '1D' | '1W' | '1M' | '3M' | 'ALL';

export interface BrainEquityPayload {
  account_id: string;
  broker_account_id: string | null;
  broker_account_name: string | null;
  current_equity: number | null;
  range: EquityRange;
  period: string;
  timeframe: string;
  points: BrainEquityPoint[];
  point_count: number;
  first_point: BrainEquityPoint | null;
  last_point: BrainEquityPoint | null;
  source: 'alpaca';
}

export interface AccountTierState {
  account_id: string;
  day: string;
  trades_executed: number;
  l2_consumed: number;
  daily_loss_pct: number;
  killed: boolean;
  kill_reason: string | null;
  profile: Record<string, unknown> | null;
  caps: {
    trades_per_day: number;
    l2_per_day: number;
    paper_only: boolean;
    daily_loss_pct: number;
  } | null;
}

export interface AccountLiveData {
  trades: BrainTrade[];
  decisions: BrainDecision[];
  equity: BrainEquityPayload | null;
  state: AccountTierState | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

async function fetchJson(url: string): Promise<Record<string, unknown>> {
  const res = await fetch(url, { headers: { 'Cache-Control': 'no-cache' } });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  const json = await res.json();
  if (!json.ok) throw new Error(json.error?.message || `API error for ${url}`);
  return json.data;
}

export function useAccountLiveData(
  accountId: string | null | undefined,
  range: EquityRange = '1M',
): AccountLiveData {
  const [trades, setTrades] = useState<BrainTrade[]>([]);
  const [decisions, setDecisions] = useState<BrainDecision[]>([]);
  const [equity, setEquity] = useState<BrainEquityPayload | null>(null);
  const [state, setState] = useState<AccountTierState | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Serialize concurrent fetches and skip the "loading" flash on polls
  // (only the first load / account switch / range switch shows the spinner).
  const firstLoadRef = useRef<boolean>(true);
  const inFlightRef = useRef<boolean>(false);

  // Phase 3 — Request versioning: incremented on every account/range change.
  // Responses stamped with a stale version are silently discarded.
  const versionRef = useRef<number>(0);

  const load = useCallback(async () => {
    if (!accountId) {
      setTrades([]);
      setDecisions([]);
      setEquity(null);
      setState(null);
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    // Phase 3 — stamp this request with the current version
    const myVersion = versionRef.current;

    if (firstLoadRef.current) setLoading(true);
    setError(null);
    try {
      const [tRes, dRes, eRes, sRes] = await Promise.allSettled([
        fetchJson(`${BASE_URL}/account/${encodeURIComponent(accountId)}/trades?limit=200`),
        fetchJson(`${BASE_URL}/account/${encodeURIComponent(accountId)}/decisions?limit=100`),
        fetchJson(
          `${BASE_URL}/account/${encodeURIComponent(accountId)}/equity` +
          `?range=${encodeURIComponent(range)}`,
        ),
        fetchJson(`${BASE_URL}/account/${encodeURIComponent(accountId)}/state`),
      ]);

      // Phase 3 — stale response rejection: if account changed while
      // we were fetching, discard ALL results from this batch.
      if (myVersion !== versionRef.current) {
        // eslint-disable-next-line no-console
        console.warn('[STALE BLOCKED] Discarding response batch v' + myVersion + ' (current v' + versionRef.current + ')');
        inFlightRef.current = false;
        return;
      }

      if (tRes.status === 'fulfilled') {
        // Phase 2 — hard account_id guard: discard cross-account leakage
        const rawTrades = (tRes.value.trades as BrainTrade[]) ?? [];
        const safeTrades = rawTrades
          .filter(t => t.account_id === accountId)
          .sort((a, b) => (a.executed_at || a.created_at || '').localeCompare(b.executed_at || b.created_at || ''));
        setTrades(safeTrades);
      } else if (firstLoadRef.current) {
        setTrades([]);
      }
      if (dRes.status === 'fulfilled') {
        // Phase 2 — hard account_id guard: discard cross-account leakage
        const rawDecisions = (dRes.value.decisions as BrainDecision[]) ?? [];
        const safeDecs = rawDecisions
          .filter(d => d.account_id === accountId)
          .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
        setDecisions(safeDecs);
      } else if (firstLoadRef.current) {
        setDecisions([]);
      }
      if (eRes.status === 'fulfilled') {
        const payload = eRes.value as unknown as BrainEquityPayload;
        // Guard: if the payload's account_id doesn't match, drop it.
        if (!payload || payload.account_id !== accountId) {
          setEquity(null);
        } else {
          // ── HARD ASSERT — last point must match current equity ──
          if (
            payload.last_point &&
            payload.current_equity != null &&
            Math.abs(payload.last_point.equity - payload.current_equity) > 100
          ) {
            // eslint-disable-next-line no-console
            console.error(
              '[EQUITY MISMATCH] last_point.equity =',
              payload.last_point.equity,
              'vs current_equity =',
              payload.current_equity,
            );
          }
          // Sort points ASC for deterministic rendering
          if (payload.points?.length) {
            payload.points.sort((a, b) => (a.t || '').localeCompare(b.t || ''));
          }
          setEquity(payload);
        }
      } else if (firstLoadRef.current) {
        setEquity(null);
      }
      if (sRes.status === 'fulfilled') {
        setState(sRes.value as unknown as AccountTierState);
      } else if (firstLoadRef.current) {
        setState(null);
      }

      // Only report a true error if every request failed on the FIRST load.
      const allFailed =
        tRes.status === 'rejected' && dRes.status === 'rejected' &&
        eRes.status === 'rejected' && sRes.status === 'rejected';
      if (allFailed && firstLoadRef.current) {
        const msg = [tRes, dRes, eRes, sRes]
          .map((r) => (r.status === 'rejected' ? String(r.reason?.message || r.reason) : ''))
          .filter(Boolean)
          .join('; ');
        setError(msg || 'Failed to load account data');
      }
    } catch (err) {
      if (firstLoadRef.current) {
        const msg = err instanceof Error ? err.message : 'Failed to load account data';
        setError(msg);
      }
    } finally {
      if (firstLoadRef.current) {
        setLoading(false);
        firstLoadRef.current = false;
      }
      inFlightRef.current = false;
    }
  }, [accountId, range]);

  // Phase 1 — Hard reset ALL state on account/range change.
  // Prevents previous account's data from flashing under the new selection.
  useEffect(() => {
    // Phase 3 — bump version so in-flight requests for the OLD account are discarded
    versionRef.current += 1;
    firstLoadRef.current = true;
    // Phase 1 — clear EVERYTHING, not just equity
    setTrades([]);
    setDecisions([]);
    setEquity(null);
    setState(null);
    setLoading(true);
    load();
  }, [load]);

  // Auto-poll every POLL_INTERVAL_MS so balances, trades, decisions, and
  // the equity graph refresh live.
  useEffect(() => {
    if (!accountId) return;
    const id = window.setInterval(() => {
      load();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [accountId, load]);

  return { trades, decisions, equity, state, loading, error, refetch: load };
}
